import { MainHeader, Section, SectionParagraph } from '@/components/shared/Content';
import { useContent, UseContentProps } from '@/hooks/useContent';
import logoImg from '@/static/assets/lemonade_goes_global/logo.png';
import { Flex, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

export const useContentFrontMatter = ({ content }: UseContentProps) => {
  return {
    ...('disclaimer' in content && { disclaimer: content.disclaimer }),
  };
};

const GlobalStyle = createGlobalStyle`
  em {
    font-style: italic;
  }
`;

const Heading1 = styled(MainHeader)`
  margin-bottom: 30px;
  margin-top: 70px;
  ${MEDIA_QUERIES.mobile`
    word-wrap: break-word;
  `}
`;

const Text = styled(SectionParagraph)`
  text-align: left;
  margin-bottom: 20px;

  strong {
    font-weight: 700;
  }
`;

const Heading2 = styled.h2`
  ${font('main', { fontSize: '2.1rem', fontWeight: 700 })}
  margin-bottom: 20px;
`;

const BulletList = styled.ul`
  ${font('main', { fontSize: '1.8rem' })}
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style-type: none;
  margin-left: 20px;
  margin-bottom: 20px;

  li::before {
    font-size: 2.2rem;
    content: '•';
    display: inline-block;
    width: 1em;
    margin-left: -1em;
  }
`;

const OrderedList = styled.ol`
  ${font('main', { fontSize: '1.8rem' })}
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style-type: none;
  margin-left: 18px;
  margin-bottom: 20px;
  list-style-type: decimal;
`;

const Cols = styled(Flex)`
  width: 100%;
  max-width: 1140px;

  div {
    flex: 1;

    .coming-soon {
      color: ${({ theme }) => theme.colors.metal};

      h2 {
        text-transform: uppercase;
        margin-bottom: 0;
      }
    }
  }
`;

const Disclaimer = styled(SectionParagraph)`
  font-size: 1.6rem;
`;

export const components = {
  h1: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading1 {...props} />,
  h2: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <Heading2 {...props} />,
  p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => <Text {...props} />,
  ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <BulletList {...props} />,
  ol: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <OrderedList {...props} />,
};

const SOCIAL = {
  facebook: '<a target="_blank" href="https://www.facebook.com/Lemonade">Facebook</a>',
  twitter: '<a target="_blank" href="https://twitter.com/lemonade_inc">Twitter</a>',
};

export interface LemonadeGoesGlobalContentProps extends UseContentProps {
  logo?: StaticImageData;
}

export const LemonadeGoesGlobalContent: React.FC<LemonadeGoesGlobalContentProps> = ({ logo = logoImg, content }) => {
  const Component = useContent({ content });
  const { disclaimer } = useContentFrontMatter({ content });

  const interpolatedDisclaimer = disclaimer
    ?.replace('{{facebook}}', SOCIAL.facebook)
    .replace('{{twitter}}', SOCIAL.twitter);

  return (
    <Section px="20px" darkBackground py={[60, 100]}>
      <GlobalStyle />
      <Cols gap="40px" flexDirection={['column', 'row']}>
        <Component components={components} />
      </Cols>

      <Flex justifyContent="center" alignItems="center" mt={[40, 70]} gap="16px" flexDirection="column">
        <Image src={logo} height={72} width={72} alt="" />
        <Disclaimer>{parse(interpolatedDisclaimer ?? '')}</Disclaimer>
      </Flex>
    </Section>
  );
};
